/** Operator actions: registering domains for customers, staff access, API keys, prices, registry operations, market. */
export const operator = {
  common: {
    reason: 'Reason',
    reasonHint: 'Kept in the audit log with your name.',
    close: 'Close',
    cancel: 'Cancel',
    choose: 'Choose…',
    loading: 'Loading…',
    optional: 'optional',
    now: 'Now',
    copy: 'Copy',
    copied: 'Copied',
    done: 'Done.',
    showOnce: 'Shown only this once. Copy it now: it cannot be shown again.',
  },

  transferIn: {
    button: 'Transfer a domain in',
    title: 'Transfer a domain in for a customer',
    description: 'The domain is at another registrar: the price on the customer account, the registrant, the transfer code; the order holds the funds until the registry completes it.',
    customer: 'Customer',
    domain: 'Domain',
    domainHint: 'The domain registered at another registrar.',
    price: 'Get price',
    quote: '{{domain}}: {{total}} · charged to {{customer}}',
    registrant: 'Registrant',
    registrantHint: 'One of the customer’s subjects with complete details (checked before funds are held).',
    noRegistrant: 'The customer has no subject yet: add one first.',
    authInfo: 'Transfer code',
    authInfoHint: 'The code the current registrar gave the registrant; sent once, never shown again.',
    submit: 'Order the transfer',
    started: 'Transfer ordered; its progress shows on the domain page.',
  },
  register: {
    button: 'Register a domain',
    title: 'Register a domain for a customer',
    description:
      'The order is placed on the customer’s account and paid from their balance, exactly as if they had ordered it themselves. The registry is asked right away.',
    customer: 'Customer',
    customerSearch: 'Name or e-mail of the customer',
    customerNone: 'No customer found',
    domain: 'Domain name',
    domainHint: 'For example tenmien.vn',
    years: 'Period',
    yearsN_one: '{{count}} year',
    yearsN_other: '{{count}} years',
    promotion: 'Promotion code',
    vnnicPromo: {
      title: 'VNNIC free registration program',
      chooseRegistrant: 'Choose the registrant below to check whether they qualify.',
      request: 'Request the VNNIC promotion code',
      recheck: 'Check again',
      issued: 'The registrant qualifies. Code {{code}} is valid until {{until}} ({{left}} left).',
      expired: 'Code {{code}} expired at {{until}}. Check again before applying the free program.',
      reused: '(an earlier check, still valid)',
      newCodeNote: 'Each registration gets a new code from VNNIC right before the domain is created; this one only confirms that the registrant qualifies and prices the order.',
      refusedReason: 'VNNIC’s reason:',
      apply: 'Apply the free program',
      applied_one: 'Applied: VNNIC fees and the service fee waived, at most {{count}} year.',
      applied_other: 'Applied: VNNIC fees and the service fee waived, at most {{count}} years.',
      missingTitle: 'The registrant’s details are incomplete',
      missingBody: 'Complete these in the registrant’s profile, then request the code again:',
      openSubject: 'Open the registrant’s profile',
      refused: 'VNNIC did not issue a promotion code for this registrant',
      fields: {
        legal_name: 'Full name / organisation name',
        address: 'Address in Vietnam (province and ward)',
        province: 'Province / city (from the administrative list)',
        ward: 'Ward / commune (from the administrative list, in the chosen province)',
        street: 'House number and street',
        identifier: 'ID card / passport number or enterprise code',
        birth_date: 'Date of birth',
        gender: 'Gender',
        establish_date: 'Founding date',
        household_code: 'Household business registration number',
        owner_name: 'Name of the household business owner',
      },
    },
    check: 'Check and get a price',
    available: '{{name}} is free to register.',
    unavailable: '{{name}} cannot be registered: {{reason}}',
    unknown: 'The registry could not be asked right now. Try again in a moment.',
    registryProblem: "Priced, but this TLD's registry connection cannot be used yet, so the registration cannot be placed.",
    reasons: {
      INVALID_NAME: 'this is not a valid domain name',
      TLD_NOT_SUPPORTED: 'we do not sell this extension',
      REGISTRY_UNAVAILABLE: 'the registry is not answering',
      REGISTRY_NOT_CONFIGURED: "this TLD's registry connection cannot be used yet",
      REGISTRY_AUTH_FAILED: 'the registrar refused the login',
      REGISTRY_ERROR: 'the registrar answered with an error',
      REGISTRY_TIMEOUT: 'the registrar did not answer in time',
      TLD_NOT_OFFERED_BY_REGISTRY: 'the registrar does not offer this extension',
      taken: 'it is already registered',
    },
    price: 'Price for {{years}}: {{total}} including tax (valid until {{until}}).',
    discount: 'Promotion discount: {{amount}}.',
    addonLine: 'fee / add-on of the TLD · {{tax}}',
    registrant: 'Registrant',
    registrantHint: 'The person or organisation the domain will belong to. Only this customer’s registrants are listed.',
    noRegistrant: 'This customer has no registrant yet. Create one on the customer’s page first.',
    nameservers: 'Name servers',
    nameserversHint: 'One per line. Leave empty to use the default ones.',
    agreement: 'Registration agreement accepted',
    agreementHint: 'The version of the registration agreement the registrant accepted, for example 2026-01.',
    submit: 'Place the order and register',
    placed: 'Order placed. {{name}} is being registered; the domain page shows the progress.',
    openDomain: 'Open the domain',
  },

  access: {
    kind: {
      CREATE_STAFF: 'Add a staff member',
      DISABLE_STAFF: 'Disable an account',
      ENABLE_STAFF: 'Enable an account',
      RESET_MFA: 'Reset the authenticator',
      UNLOCK_STAFF: 'Lift a sign-in lock',
      GRANT_ROLE: 'Give a role',
      REVOKE_ROLE: 'Take a role away',
      ISSUE_API_CREDENTIAL: 'Issue an API key',
      CHANGE_REPORTING_CONSUMER: 'Change what a place receives',
      ISSUE_REPORTING_KEY: 'Create a reporting key',
      ROTATE_REPORTING_KEY: 'Replace a reporting key',
      RESET_REPORTING_SYNC: 'Send a kind of data again from the start',
    },
    status: {
      PENDING: 'Waiting for approval',
      APPROVED: 'Approved, not carried out yet',
      REJECTED: 'Refused',
      EXPIRED: 'Timed out',
      EXECUTED: 'Carried out',
      CANCELLED: 'Withdrawn',
    },
    pendingTitle: 'Access changes waiting',
    pendingHint: 'Every change to staff access or API keys needs a second approval before it is carried out.',
    none: 'Nothing is waiting.',
    requestedBy: 'Asked by {{name}}',
    decidedBy: 'Decided by {{name}}',
    selfApproved: 'Approved by the same person with their authenticator code.',
    target: 'Applies to',
    proposed: 'The change is recorded. It is carried out once it is approved.',
    selfApproveTitle: 'Approve your own change',
    selfApproveHint:
      'You may approve changes you asked for when nobody else can. Enter the current 6-digit code from your authenticator app to confirm it is you.',
    waitSecond: 'Another person who manages staff access must approve it. They will find it in Approvals.',
    openApprovals: 'Open approvals',
    totp: 'Authenticator code',
    approveApply: 'Approve and carry out',
    approve: 'Approve',
    reject: 'Refuse',
    decisionReason: 'Why you approve or refuse',
    apply: 'Carry out',
    applyTitle: 'Carry out the approved change',
    withdraw: 'Withdraw',
    withdrawReason: 'Why you withdraw it',
    applied: 'Carried out.',
    password: 'First password',
    passwordHint: 'At least 12 characters. Leave empty and one is created for you.',
    totpUri: 'Authenticator set-up link',
    totpUriHint:
      'Give it to the person privately: they add it to their authenticator app (paste it, or turn it into a QR code). It is the only way to sign in.',
    initialPassword: 'First password',
    apiKey: 'API key',
    apiKeyHint: 'Give it to the customer privately. The platform keeps only a fingerprint of it.',
    newStaff: 'Add a staff member',
    newStaffDescription: 'The account is created once the change is approved. You receive the sign-in details to hand over.',
    email: 'E-mail address',
    displayName: 'Full name',
    roles: 'Roles',
    rolesHint: 'Given for the whole platform. You can add a role limited to one customer later.',
    actions: 'Change access',
    disable: 'Disable the account',
    disableHint: 'The person is signed out everywhere and can no longer sign in.',
    enable: 'Enable the account',
    resetMfa: 'Reset the authenticator',
    resetMfaHint: 'For a lost or replaced phone. The person is signed out and receives a new set-up link.',
    unlock: 'Lift the sign-in lock',
    unlockHint: 'After too many wrong passwords the account is locked for a while. This lifts it now.',
    grantRole: 'Give a role',
    revokeRole: 'Take a role away',
    role: 'Role',
    scope: 'Applies to',
    scopeGlobal: 'The whole platform',
    scopeCustomer: 'One customer',
    scopeAccount: 'Account',
    submitProposal: 'Ask for the change',
  },

  keys: {
    issue: 'Issue an API key',
    issueDescription:
      'A customer uses the key to work with us from its own systems (ordering, renewing, managing domains). It is created once the request is approved; the key itself is shown once.',
    owner: 'Account',
    ownerSearch: 'Name of the account',
    expires: 'Valid until',
    expiresHint: 'Leave empty for no end date.',
    revoke: 'Revoke',
    revokeTitle: 'Revoke this API key',
    revokeDescription: 'The key stops working at once. Programs using it get refused until they use a new key.',
    revoked: 'The key is revoked.',
  },

  tax: {
    new: 'New VAT rate',
    newVersion: 'New version',
    description:
      'The VAT rate of one tax code (e.g. VAT10 = 10%). A new version starts at the time you choose and replaces the one in force then; quotes already given keep their rate. Each product chooses its tax code; TLD and add-on prices that name none use the default code.',
    code: 'Tax code',
    codeHint: 'Capital letters and digits, e.g. VAT10. KCT (not taxable) is a category, not a code.',
    percent: 'Rate (%)',
    source: 'Legal basis',
    sourceHint: 'The regulation that sets the rate, e.g. VAT Law 48/2024/QH15.',
    publish: 'Publish rate',
    endTitle: 'End VAT rate',
    endDescription: 'From the end, a product with this tax code cannot be quoted until a new version is in force or it uses another code.',
    makeDefault: 'Make default',
    defaultDescription: 'TLD and add-on prices that name no tax code will be taxed at {{code}}. Quotes already given do not change.',
    defaultSet: 'The default tax code is now {{code}}.',
  },
  pricing: {
    newCost: 'New registry price',
    newCostDescription:
      'What the registry charges us. A new price starts at the time you choose and replaces the one in force then; prices already quoted to customers do not change. A cost belongs to the TLD, never to a connection or environment (OT&E / production): when the TLD moves to another registry with another cost, publish a new version for the TLD.',
    costGrid: {
      description: 'The TLD\'s registry cost for every operation in one go; empty = keep. A new price starts at the time you choose and replaces the one in force; prices already quoted do not change.',
      years: 'Period',
      yearsValue_one: '{{count}} year',
      yearsValue_other: '{{count}} years',
      all: 'Apply to every operation',
      apply: 'Apply',
      op: 'Operation',
      current: 'Cost now',
      amount: 'New cost',
      converted: 'Converted (VND)',
      keep: 'Keep',
      none: 'None',
      since: 'since {{date}} · v{{version}}',
      noFx: 'No exchange rate',
      save_one: 'Save {{count}} operation',
      save_other: 'Save {{count}} operations',
      nothing: 'No operation changed yet.',
      saved_one: 'Saved the cost of {{count}} operation.',
      saved_other: 'Saved the cost of {{count}} operations.',
      pickFirst: 'Choose a TLD to set the cost of every operation.',
    },
    registry: 'Registry',
    tld: 'Extension',
    anyTld: 'Every extension',
    operation: 'Operation',
    years: 'Years',
    anyYears: 'Any period (price per year)',
    amount: 'Amount',
    currency: 'Currency',
    from: 'Starts',
    fromHint: 'Leave empty to start now. A price never starts in the past.',
    until: 'Ends',
    untilHint: 'Leave empty to end now.',
    end: 'End',
    endTitle: 'End this price',
    endDescription: 'After the end, this price no longer applies to new quotes. Quotes already given keep their price.',
    published: 'Published as version {{version}}.',
    newBook: 'New price book',
    newBookDescription: 'A price book sets the selling prices for a group of customers. It starts as a draft: no customer is priced from it until you make it active.',
    code: 'Code',
    appliesTo: 'Used for',
    tier: 'Customer level',
    tierHint: 'A tier of the catalogue: its book prices every product for the tier\'s customers.',
    customer: 'Customer',
    status: 'Status',
    setStatus: 'Change status',
    activate: 'Make active',
    retire: 'Retire',
    statusTitle: 'Change the status of this price book',
    statusDraft: 'Draft',
    statusActive: 'Active',
    statusRetired: 'Retired',
    retiredForever: 'A retired price book cannot be used again.',
    newRule: 'New selling price',
    newRuleDescription:
      'A new version of the selling price for this operation and extension. It replaces the price in force from its start; quotes already given keep their price.',
    ruleType: 'How the price is set',
    value: 'Value',
    valueHint: 'A fixed price, an amount added to the cost, or a percentage added to the cost.',
    min: 'Lowest price',
    max: 'Highest price',
    priority: 'Priority',
    priorityHint: 'Lower numbers win when several prices apply.',
    ruleTypes: {
      FIXED_PRICE: 'Fixed price',
      MARKUP_FIXED: 'Cost plus an amount',
      MARKUP_PERCENT: 'Cost plus a percentage',
    },
    appliesToValues: {
      TIER: 'Every customer of a tier',
      CUSTOMER_CONTRACT: 'One customer (its own prices for a few products)',
    },
    operations: {
      REGISTER: 'Registration',
      RENEW: 'Renewal',
      TRANSFER: 'Transfer',
      RESTORE: 'Restore',
      REDEMPTION: 'Redemption',
      BACKORDER: 'Backorder',
      AUCTION: 'Auction',
      ADDON: 'Add-on',
    },
    addon: 'Add-on service',
    book: 'Price book',
    forOperation: 'For operation',
    forOperationHint: 'A price for one operation (e.g. registration differs from renewal); leave "every operation" when it is the same.',
    everyOperation: 'Every operation of the add-on',
    amountBasis: 'Basis',
    amountBases: { FIXED: 'Fixed amount', PERCENT_OF_REGISTRY_COST: "% of the operation's registry cost" },
    amountBasisHint: {
      FIXED: 'Amount for one year (per-year add-ons are multiplied by the years of the operation) or for once.',
      PERCENT_OF_REGISTRY_COST: "Percentage of the registry cost of the operation it comes with (years included), e.g. 2 %.",
    },
    percent: 'Percentage (%)',
    addonYearsHint: 'Usually 1: the price of one year.',
    alsoTlds: 'Same price for other TLDs',
    alsoTldsHint: 'Each selected TLD gets its own price row (there is no price for every TLD).',
    addonCostDescription:
      "The add-on's price for this TLD (a pass-through fee or its cost). Without a selling price in the price book the customer pays this amount. A new price replaces the one in force from its start; quotes already given keep theirs.",
    addonPerTld: 'Add-on prices are set per TLD.',
    addonElsewhere: 'Fees and add-on prices are set on the TLD page, tab Prices & services.',
    taxCategory: 'Tax category',
    taxCode: 'VAT rate code',
    taxCodeHint: 'Empty = the default VAT rate.',
    taxCategories: { VAT: 'VAT', KCT: 'Not subject to VAT (KCT)' },
  },

  promotions: {
    create: 'New promotion',
    createDescription: 'A code customers enter to get a discount. Quotes already given keep their price.',
    edit: 'Edit',
    editDescription: 'Once the code has been used, only its end date and the number of uses can change.',
    disable: 'Disable',
    disableTitle: 'Disable this code',
    disableDescription: 'The code stops giving a discount on new quotes at once. Quotes already given keep their discount.',
    disabled: 'Disabled',
    disabledOn: 'Disabled {{when}}: {{reason}}',
    code: 'Code',
    codeHint: 'Letters, digits, - or _. Stored in capitals.',
    discountType: 'Discount',
    percent: 'Percentage',
    fixed: 'Fixed amount',
    value: 'Value',
    currency: 'Currency',
    tld: 'Extension',
    anyTld: 'Every extension',
    operation: 'Operation',
    anyOperation: 'Every operation',
    starts: 'Starts',
    ends: 'Ends',
    maxUses: 'Most uses',
    maxUsesHint: 'Leave empty for no limit.',
    saved: 'Saved.',
    created: 'Promotion {{code}} created.',
  },

  registry: {
    resolve: 'Resolve',
    resolveTitle: 'Resolve this difference',
    resolveDescription: 'Say what you found and what was done. The difference is closed and the audit log keeps your explanation.',
    outcome: 'What you found',
    outcomes: {
      REGISTRY_CORRECT: 'The registry is right: update our record from the registry',
      FIXED: 'Corrected by hand (at the registry or here)',
      NO_ACTION: 'Nothing to do: a false alarm or an accepted difference',
    },
    commandStays: 'The registry command linked to this difference keeps its state; this only records your finding.',
    resolved: 'Resolved.',
    resolvedBy: 'Resolved by {{name}} {{when}}',
    ack: 'Confirm to the registry',
    ackTitle: 'Confirm this message to the registry',
    ackDescription: 'The registry keeps a message in its queue until we confirm we received it. Use this when the confirmation did not reach it.',
    acked: 'The registry has the confirmation.',
    processed: 'Mark as handled',
    processedTitle: 'Mark this message as handled',
    processedDescription: 'Say what was done about it.',
    markedProcessed: 'Marked as handled.',
  },

  market: {
    cancelBackorder: 'Cancel the backorder',
    cancelBackorderDescription: 'Only a backorder that is not yet planned for a release can be cancelled. The money held for it is released to the customer.',
    backorderCancelled: 'The backorder is cancelled and the money released.',
    open: 'Open now',
    openTitle: 'Open this auction now',
    openDescription: 'For when opening is late. An auction never opens before its published start time.',
    close: 'Close now',
    closeTitle: 'Close this auction now',
    closeDescription:
      'For when closing is late. The highest bid wins if it meets the reserve price. An auction never closes before its published end.',
    opened: 'The auction is open.',
    closed: 'The auction is closed: {{status}}.',
  },
};
